import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as LucideIcons from 'lucide-react';

interface SearchBarProps {
  onSearchChange: (query: string) => void;
  placeholder?: string;
  width?: string;
  dataTarget?: string;
  debounceMs?: number;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  onSearchChange,
  placeholder = 'Search...',
  width = '300px',
  dataTarget = 'search-input',
  debounceMs = 300,
}) => {
  // Local state for immediate input feedback
  const [localValue, setLocalValue] = useState('');
  const onSearchChangeRef = useRef(onSearchChange);

  // Keep ref up to date
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  // Debounce the onChange callback
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onSearchChangeRef.current(localValue);
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [localValue, debounceMs]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setLocalValue('');
    onSearchChange('');
  }, [onSearchChange]);

  return (
    <div className="relative" style={{ width }}>
      <LucideIcons.Search 
        size={20} 
        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
      />
      <input
        type="text"
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        className="w-full pl-10 pr-3 py-1.5 border border-gray-400 rounded-lg text-sm bg-white text-gray-900 transition-shadow focus:outline-none focus:ring-2 focus:ring-blue-100 focus:shadow-[0_0_0_3px_rgba(102,126,234,0.1)]"
        style={{
          paddingRight: localValue ? '40px' : '12px',
        }}
        data-pp-at-target={dataTarget}
      />
      {localValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none text-gray-500 cursor-pointer p-1 flex items-center justify-center rounded transition-all hover:bg-gray-100 hover:text-gray-700"
          aria-label="Clear search"
        >
          ✕
        </button>
      )}
    </div>
  );
};
